import {
  Configuration,
  CreateOrModifyPossessorRequest,
  ErrorDto,
  GetPossessorByUidRequest,
  GetPossessorNameByUidRequest,
  Middleware,
  ModifyPossessorRequestDto,
  OrganizationType,
  PossessorByUidResponseDto,
  PossessorDto,
  PossessorDtoTypeEnum,
  GetPossessorPageRequest,
  PossessorResponseDto,
  PossessorRestControllerApi,
  querystring,
  PossessorRequestDto,
  RegistrationDto,
} from '../generated/animare-management-api'

import { PossessorPageResponseDto } from '../generated/animare-management-api/models/PossessorPageResponseDto'

import { QueryClient, UseMutationResult, useMutation, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { AnimareApiKeys } from '../data/apiKeys'
import { ApiErrorMiddleware } from '../middleware/api-error-middleware'
import { API_BASE_URL } from '../setup'

export function usePossessor() {
  const { t } = useTranslation('common')

  const api = new PossessorRestControllerApi({
    basePath: API_BASE_URL,
    middleware: [new ApiErrorMiddleware()] as Middleware[],
    queryParamsStringify: querystring,
    credentials: 'include',
  } as Configuration)

  const PAGE_SIZE = 200

  async function fetchPossessorWithFallback(
    api: PossessorRestControllerApi,
    possessorRequestDto: PossessorRequestDto,
  ): Promise<PossessorResponseDto> {
    try {
      let pageIndex = 0
      const first = await api.getPossessorPage({
        page: pageIndex,
        size: PAGE_SIZE,
        possessorRequestDto,
      } as any)

      const page = first?.registrationsPage
      if (!page || !Array.isArray(page.items)) {
        // käsitellään kuin “ei tueta sivutusta” → triggeröi fallback
        const err404 = Object.assign(new Error('Invalid page payload'), { status: 404 })
        throw err404
      }

      const possessor = first.possessor
      const all: RegistrationDto[] = [...page.items]
      let hasNext = page.hasNext

      while (hasNext) {
        pageIndex += 1
        const next = await api.getPossessorPage({
          page: pageIndex,
          size: PAGE_SIZE,
          possessorRequestDto,
        } as any)

        const nextPage = next?.registrationsPage
        if (!nextPage || !Array.isArray(nextPage.items)) {
          const err404 = Object.assign(new Error('Invalid page payload'), { status: 404 })
          throw err404
        }

        all.push(...nextPage.items)
        hasNext = nextPage.hasNext
      }

      // Palauta täsmälleen sama muoto kuin legacyssä (PossessorResponseDto),
      // mutta rekisteröinnit koottuna kaikilta sivuilta
      return {
        possessor,
        registrations: all,
      }
    } catch (err: any) {
      const status = err?.status ?? err?.response?.status ?? err?.cause?.status
      if (status === 404) {
        // Fallback legacyyn
        return api.getPossessor({ possessorRequestDto })
      }
      throw err
    }
  }

  /**
   * Fetches possessor and past registrations (if needed).
   * @param possessorIdentifier
   * @param fetchPastRegistrations
   * @returns
   */
  const getPersonPossessor = ({
    onSuccess,
    onError,
    onSettled,
    queryClient,
  }: {
    onSuccess: (response: PossessorResponseDto) => void
    onError: (error: ErrorDto) => void
    onSettled: () => void
    queryClient: QueryClient
  }): UseMutationResult<
    PossessorResponseDto,
    ErrorDto,
    { personIdentifier: string; fetchPastRegistrations: boolean },
    unknown
  > => {
    const getPersonPossessorMutation = useMutation({
      mutationFn: ({
        personIdentifier,
        fetchPastRegistrations,
      }: {
        personIdentifier: string
        fetchPastRegistrations: boolean
      }) =>
        fetchPossessorWithFallback(api, {
          possessorType: PossessorDtoTypeEnum.Person,
          personIdentifier,
          fetchPastRegistrations,
        } as PossessorRequestDto),
      onSuccess: (response: PossessorResponseDto) => {
        queryClient.invalidateQueries({ queryKey: [AnimareApiKeys.POSSESSOR] })
        onSuccess(response)
      },
      onError: (error: ErrorDto) => {
        onError(error)
        console.error(error)
      },
      onSettled: onSettled,
    })

    return getPersonPossessorMutation
  }

  const getOrganizationPossessor = ({
    onSuccess,
    onError,
    onSettled,
    queryClient,
  }: {
    onSuccess: (response: PossessorResponseDto) => void
    onError: (error: ErrorDto, organizationIdentifier: string) => void
    onSettled: () => void
    queryClient: QueryClient
  }): UseMutationResult<
    PossessorResponseDto,
    ErrorDto,
    {
      organizationIdentifier: string
      fetchPastRegistrations: boolean
    },
    unknown
  > => {
    const getOrganizationPossessorMutation = useMutation({
      mutationFn: ({
        organizationIdentifier,
        fetchPastRegistrations,
      }: {
        organizationIdentifier: string
        fetchPastRegistrations: boolean
      }) =>
        fetchPossessorWithFallback(api, {
          possessorType: PossessorDtoTypeEnum.Organization,
          organizationIdentifier,
          fetchPastRegistrations,
        }),
      onSuccess: (response) => {
        queryClient.invalidateQueries({ queryKey: [AnimareApiKeys.POSSESSOR] })
        onSuccess(response)
      },
      onError: (
        error: ErrorDto,
        queryVars: {
          organizationIdentifier: string
          fetchPastRegistrations: boolean
        },
      ) => {
        onError(error, queryVars.organizationIdentifier)

        console.error(error)
      },
      onSettled: onSettled,
    })

    return getOrganizationPossessorMutation
  }

  async function fetchPossessorResponseByUidWithPagination(
    api: PossessorRestControllerApi,
    uid: string,
    fetchPastRegistrations = true,
  ): Promise<PossessorResponseDto> {
    const firstResponse = await api.getPossessorByUid({
      uid,
      fmt: 'form',
    } as GetPossessorByUidRequest)

    const entity = firstResponse as PossessorDto

    const possessor = entity

    let possessorRequestDto: PossessorRequestDto

    if (possessor.type === 'person') {
      if (!possessor.personIdentifier) {
        throw new Error('Person possessor missing personIdentifier')
      }
      possessorRequestDto = {
        possessorType: PossessorDtoTypeEnum.Person,
        personIdentifier: possessor.personIdentifier,
        fetchPastRegistrations,
      }
    } else if (possessor.type === 'organization') {
      if (!possessor.organizationIdentifier) {
        throw new Error('Organization possessor missing organizationIdentifier')
      }
      possessorRequestDto = {
        possessorType: PossessorDtoTypeEnum.Organization,
        organizationIdentifier: possessor.organizationIdentifier,
        fetchPastRegistrations,
      }
    } else {
      throw new Error(`Unknown possessor type: ${possessor.type}`)
    }
    try {
      let pageIndex = 0
      const first = await api.getPossessorPage({
        page: pageIndex,
        size: PAGE_SIZE,
        possessorRequestDto,
      } as any)

      const page = first?.registrationsPage
      if (!page || !Array.isArray(page.items)) {
        // käsitellään kuin “ei tueta sivutusta” → triggeröi fallback
        const err404 = Object.assign(new Error('Invalid page payload'), { status: 404 })
        throw err404
      }

      const possessor = first.possessor
      const all: RegistrationDto[] = [...page.items]
      let hasNext = page.hasNext

      while (hasNext) {
        pageIndex += 1
        const next = await api.getPossessorPage({
          page: pageIndex,
          size: PAGE_SIZE,
          possessorRequestDto,
        } as any)

        const nextPage = next?.registrationsPage
        if (!nextPage || !Array.isArray(nextPage.items)) {
          const err404 = Object.assign(new Error('Invalid page payload'), { status: 404 })
          throw err404
        }

        all.push(...nextPage.items)
        hasNext = nextPage.hasNext
      }

      // Palauta täsmälleen sama muoto kuin legacyssä (PossessorResponseDto),
      // mutta rekisteröinnit koottuna kaikilta sivuilta
      return {
        possessor,
        registrations: all,
      }
    } catch (err: any) {
      const status = err?.status ?? err?.response?.status ?? err?.cause?.status
      if (status === 404) {
        // Fallback legacyyn
        return api.getPossessor({ possessorRequestDto })
      }
      throw err
    }
  }
  /**
   *
   * @param uid
   * @returns
   */
  const getPossessorName = (uid: string) => {
    return api.getPossessorNameByUid({ uid } as GetPossessorNameByUidRequest)
  }

  /**
   *
   * @param uid
   * @returns
   */
  const getPossessorByUid = ({
    uid,
    enabled = true,
  }: {
    uid: string
    fmt?: string
    enabled?: boolean
  }): { error: ErrorDto | null; data?: PossessorDto } => {
    const { data, error } = useQuery<PossessorDto, ErrorDto>({
      queryKey: [AnimareApiKeys.POSSESSOR, uid],
      queryFn: ({ queryKey }) =>
        api
          .getPossessorByUid({ uid: `${queryKey[1]}` } as GetPossessorByUidRequest)
          .then((result) => result as PossessorDto),
      enabled,
    })

    return { data, error }
  }

  /**
   *
   * @param uid
   * @returns
   */
  const getPossessorResponseByUid = ({
    uid,
    fetchPastRegistrations = true,
    enabled = true,
  }: {
    uid: string
    fetchPastRegistrations?: boolean
    enabled?: boolean
  }): { error: ErrorDto | null; data?: PossessorResponseDto; isLoading: boolean } => {
    const { data, error, isLoading } = useQuery<PossessorResponseDto, ErrorDto>({
      queryKey: [AnimareApiKeys.POSSESSOR, uid, 'with-registrations', fetchPastRegistrations],
      queryFn: () => fetchPossessorResponseByUidWithPagination(api, uid, fetchPastRegistrations),
      enabled,
      gcTime: 0,
    })

    return { data, error, isLoading }
  }

  /**
   * Creates a new possessor.
   * @param possessorData
   * @returns
   */

  const createPossessor = ({
    onSuccess,
    onError,
    queryClient,
  }: {
    onSuccess: (res: PossessorDto) => void
    onError: (error: ErrorDto) => void
    queryClient: QueryClient
  }): UseMutationResult<PossessorDto, ErrorDto, ModifyPossessorRequestDto, unknown> => {
    const createPossessorMutation = useMutation({
      mutationFn: (newPossessor: ModifyPossessorRequestDto) =>
        api.createOrModifyPossessor({
          modifyPossessorRequestDto: newPossessor,
        } as CreateOrModifyPossessorRequest),
      onSuccess: (res: PossessorDto) => {
        // Invalidate and refetch
        queryClient.invalidateQueries({ queryKey: [AnimareApiKeys.POSSESSOR] })
        onSuccess(res)
      },
      onError: (error: ErrorDto) => {
        console.error(error)
        onError(error)
      },
    })
    return createPossessorMutation
  }

  /**
   * Updates possessor details
   * @param possessorDto
   * @returns
   */

  const updatePossessor = ({
    onSuccess,
    onError,
    queryClient,
  }: {
    onSuccess: () => void
    onError: (error: ErrorDto) => void
    queryClient: QueryClient
  }): UseMutationResult<PossessorDto, ErrorDto, ModifyPossessorRequestDto, unknown> => {
    const updatePossessorMutation = useMutation({
      mutationFn: (request: ModifyPossessorRequestDto) =>
        api.createOrModifyPossessor({
          modifyPossessorRequestDto: request,
        } as CreateOrModifyPossessorRequest),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [AnimareApiKeys.POSSESSOR] })
        onSuccess()
      },
      onError: (error: ErrorDto) => {
        console.error(error)
        onError(error)
      },
    })

    return updatePossessorMutation
  }

  const getOrganizationTypeName = (organizationType: OrganizationType | undefined) => {
    if (organizationType === undefined) {
      return t('common.organizationType.unknown')
    }
    return t(`common.organizationType.${organizationType}`)
  }

  return {
    getPersonPossessor,
    getOrganizationPossessor,
    getPossessorByUid,
    getPossessorResponseByUid,
    getPossessorName,
    createPossessor,
    updatePossessor,
    getOrganizationTypeName,
  }
}
